import { Typography } from 'antd';

import type { ModelExpenseByArticlesReport } from '@app/api/Analytics';
import type { ColumnDataType } from '@app/pages/Analytics/ExpenseByArticlesReport/types';
import { divideAmount } from '@app/utils';

const { Text } = Typography;

const generateGroupedColumns = () => {
  return [
    {
      title: 'Расходы',
      dataIndex: 'article',
      width: 400,
    },
    {
      title: 'Дата',
      dataIndex: 'date',
      width: 120,
    },
    {
      title: 'Итого',
      dataIndex: 'total',
      width: 120,
    },
  ];
};

export const transformExpenseByArticlesReportToTableData = (
  data: ModelExpenseByArticlesReport | undefined
): {
  dataSource: ColumnDataType[];
  columns: ReturnType<typeof generateGroupedColumns>;
  expandedRowKeys: string[];
} => {
  if (!data) {
    return { dataSource: [], columns: [], expandedRowKeys: [] };
  }

  const articleMap = data.ArticleWithNames ?? {};

  const grouped: Record<string, ColumnDataType> = {};
  const totalByGroup: Record<string, number> = {};
  let globalTotal = 0;

  data.ExpensesByArticles?.forEach((row) => {
    const articleLabelRaw = articleMap[row.ArticleId];
    const articleLabel = articleLabelRaw ?? 'Статья не найдена';

    const [groupKey] = articleLabel.toString().split(':');

    row.ExpensesByComments?.forEach((comment, idx) => {
      const childRow: ColumnDataType = {
        key: `row-${row.ArticleId}-${idx}`,
        article: comment.Comment,
        date: new Date(comment.Timestamp).toLocaleString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
        total: divideAmount(comment.Amount),
      };

      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          key: `group-${groupKey}`,
          article: groupKey,
          total: '0',
          children: [],
        };
      }

      grouped[groupKey].children!.push(childRow);
    });

    totalByGroup[groupKey] = (totalByGroup[groupKey] || 0) + row.Amount;
    globalTotal += row.Amount;
  });

  const expandedRowKeys: string[] = [];

  const dataSource: ColumnDataType[] = Object.entries(grouped).map(
    ([groupKey, group]) => {
      const isUnknownArticle = groupKey === 'Статья не найдена';

      expandedRowKeys.push(group.key);

      return {
        ...group,
        article: isUnknownArticle ? (
          <Text type="secondary">Статья не найдена</Text>
        ) : (
          group.article
        ),
        total: divideAmount(totalByGroup[groupKey]),
      };
    }
  );

  dataSource.push({
    key: 'total',
    article: 'Итого',
    total: divideAmount(globalTotal),
  });

  const columns = generateGroupedColumns();

  return { dataSource, columns, expandedRowKeys };
};
